import type { AxiosError, AxiosInstance, Method as AxiosMethod, AxiosRequestConfig } from 'axios';
import axios from 'axios';
import { notifyError } from '@/libs/bugsnag';
import type { ISession } from '@/types/auth';

/**
 * A transport-level failure: the request never got an HTTP response (network
 * down, CORS, DNS, offline). Distinct from an HTTP error status, which is
 * rethrown as a plain `Error` carrying `status`. Callers narrow with
 * `instanceof` instead of duck-typing a flag.
 */
export class ConnectionError extends Error {
  /** Kept for consumers that only see the serialised error shape. */
  readonly isConnectionError = true;

  constructor() {
    super('Connection failed');
    this.name = 'ConnectionError';
  }
}

/**
 * Retained until explicit sign-in so reloads cannot restore a signed-out session.
 */
export const SKIP_AUTO_LOGIN_ONCE_KEY = 'spoton:skipAutoLoginOnce';

enum HTTP_METHOD {
  GET = 'get',
  POST = 'post',
  PUT = 'put',
  PATCH = 'patch',
  DELETE = 'delete',
}

function addMultiPartContentType(config?: AxiosRequestConfig): AxiosRequestConfig {
  return {
    ...(config || {}),
    headers: {
      ...(config?.headers || {}),
      'Content-Type': 'multipart/form-data',
    },
  };
}

function getErrorMessage(error: AxiosError | Error): string {
  if (axios.isAxiosError(error)) {
    if (error.response)
      return `HTTP ${error.response.status}: ${JSON.stringify(error.response.data)}`;
    if (error.request) return 'No response received from server';
    return `Axios error: ${error.message}`;
  }
  return `Unknown error: ${error.message}`;
}

function getApiBaseURL(): string {
  const raw = import.meta.env.VITE_PUBLIC_API_URL as string | undefined;
  if (!raw) throw new Error('VITE_PUBLIC_API_URL is not set');
  const url = new URL(raw);
  url.pathname = '/api/v1/';
  return url.toString();
}

class ApiService {
  private static axiosInstance: AxiosInstance = axios.create({
    baseURL: getApiBaseURL(),
    withCredentials: true,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      // temporary header to identify the user
      // ...(import.meta.env.PROD ? {} : { 'X-User-ID': 'dev-user' }),
    },
  });

  private static interceptorsInitialized = false;
  private static signingOut = false;

  private static ensureInterceptors() {
    if (ApiService.interceptorsInitialized) return;
    ApiService.axiosInstance.interceptors.response.use(
      (res) => {
        // If backend includes X-User-ID header, propagate it to all next requests
        const headers = res.headers as Record<string, string | undefined>;
        const userId = headers?.['x-user-id'];
        if (userId) {
          HttpService.setHeader('X-User-ID', userId);
        }
        return res;
      },
      (error: unknown) => {
        if (axios.isAxiosError(error)) {
          // Session failures belong to LoginGuard/sign-out; redirecting here
          // would silently log the user back in or leave sign-out pending.
          if (
            error.response?.status === 401 &&
            error.config?.url !== '/session/me' &&
            !ApiService.signingOut
          ) {
            HttpService.getSession(window.location.href);
            return new Promise(() => {});
          }
        }
        return Promise.reject(error);
      },
    );
    ApiService.interceptorsInitialized = true;
  }

  // HttpService.setHeader('Authorization', `Bearer ${token}`)
  setHeader(key: string, value: string) {
    ApiService.axiosInstance.defaults.headers.common[key] = value;
  }

  // HttpService.removeHeader('Authorization')
  removeHeader(key: string) {
    delete ApiService.axiosInstance.defaults.headers.common[key];
  }

  private async request<T = unknown, R = unknown>(
    method: AxiosMethod,
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
    queryParams?: object,
    onProgress?: (progress: number) => void,
  ): Promise<R> {
    try {
      ApiService.ensureInterceptors();
      const finalConfig: AxiosRequestConfig = {
        ...config,
        method,
        url,
        data,
        params: queryParams ?? config?.params,
        onUploadProgress: onProgress
          ? (progressEvent) => {
              if (progressEvent.total) {
                const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                onProgress(progress);
              }
            }
          : undefined,
      };
      const response = await ApiService.axiosInstance.request<R>(finalConfig);
      return response.data;
    } catch (error) {
      // Check if this is a connection error (no response = network/CORS/offline issues)
      if (axios.isAxiosError(error) && !error.response) {
        throw new ConnectionError();
      }

      // Rewrap into a plain Error so raw AxiosErrors never leak upward, but
      // carry the HTTP status so callers can classify the failure (expected
      // 4xx vs unexpected 5xx) without depending on `axios.isAxiosError`,
      // which is always false once the error has been rewrapped.
      const formattedError = getErrorMessage(error as AxiosError);
      const rethrown = new Error(formattedError) as Error & { status?: number };
      if (axios.isAxiosError(error) && error.response) {
        rethrown.status = error.response.status;
      }
      throw rethrown;
    }
  }

  async get<T = undefined, R = unknown>(
    url: string,
    queryParams?: object,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.request<T, R>(HTTP_METHOD.GET, url, undefined, config, queryParams);
  }

  async post<T = unknown, R = unknown>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.request<T, R>(HTTP_METHOD.POST, url, data, config);
  }

  async postWithMultiPart<T = unknown, R = unknown>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
    onProgress?: (progress: number) => void,
  ): Promise<R> {
    return this.request<T, R>(
      HTTP_METHOD.POST,
      url,
      data,
      addMultiPartContentType(config),
      undefined,
      onProgress,
    );
  }

  async put<T = unknown, R = unknown>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.request<T, R>(HTTP_METHOD.PUT, url, data, config);
  }

  async putWithMultiPart<T = unknown, R = unknown>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.request<T, R>(HTTP_METHOD.PUT, url, data, addMultiPartContentType(config));
  }

  async patch<T = unknown, R = unknown>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.request<T, R>(HTTP_METHOD.PATCH, url, data, config);
  }

  async patchWithMultiPart<T = unknown, R = unknown>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.request<T, R>(HTTP_METHOD.PATCH, url, data, addMultiPartContentType(config));
  }

  async delete<T = undefined, R = unknown>(
    url: string,
    data?: T,
    queryParams?: object,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.request<T, R>(HTTP_METHOD.DELETE, url, data, config, queryParams);
  }

  /**
   * Build an absolute API URL for a transport that cannot reuse the axios
   * instance — notably the browser `EventSource`, which takes a URL string
   * and cannot set custom headers. Reads the same `VITE_PUBLIC_API_URL`
   * boundary as every other call so feature code never touches
   * `import.meta.env` directly. `path` may start with or without a slash.
   */
  buildApiUrl(path: string): string {
    const base = getApiBaseURL(); // e.g. https://host/api/v1/
    return new URL(path.replace(/^\/+/, ''), base).toString();
  }

  /**
   * Absolute API URL for a Mapbox tile template. Concatenates instead of going
   * through `buildApiUrl`: the WHATWG URL parser percent-encodes the
   * `{z}/{x}/{y}` placeholders Mapbox GL substitutes verbatim.
   */
  buildTileUrl(path: string): string {
    return `${getApiBaseURL()}${path.replace(/^\/+/, '')}`;
  }

  /**
   * Whether `url` targets our API origin. Lets consumers that fetch outside the
   * axios instance (Mapbox GL) decide whether to attach the session cookie,
   * without reading the Vite env themselves.
   */
  isApiRequestUrl(url: string): boolean {
    try {
      return new URL(url).origin === new URL(getApiBaseURL()).origin;
    } catch {
      // Relative or malformed: not an absolute call to the API origin.
      return false;
    }
  }

  // Auth methods
  // LoginGuard owns authentication failures from the session probe.
  async getSessionMe(): Promise<ISession> {
    return this.get<undefined, ISession>('/session/me');
  }

  // Start login flow (top-level navigation), then redirect back to returnTo
  getSession(returnTo?: string): void {
    const target = returnTo ?? `${window.location.origin}/`;
    try {
      sessionStorage.removeItem(SKIP_AUTO_LOGIN_ONCE_KEY);
    } catch (error) {
      notifyError(error, { context: 'auth:skip-auto-login-flag:clear' });
    }
    ApiService.signingOut = false;
    const sessionUrl = new URL('session', getApiBaseURL());
    sessionUrl.searchParams.set('return_url', target);
    window.location.assign(sessionUrl.toString());
  }

  // Native gateway logout clears its cookies and opens Authentik's account/logout screen.
  async deleteSessionMe(): Promise<void> {
    ApiService.signingOut = true;
    this.removeHeader('X-User-ID');
    try {
      sessionStorage.setItem(SKIP_AUTO_LOGIN_ONCE_KEY, '1');
    } catch (error) {
      notifyError(error, { context: 'auth:skip-auto-login-flag:write' });
    }
    window.location.assign(new URL('/oauth2/logout', getApiBaseURL()).toString());
  }
}

const HttpService = new ApiService();
export { HttpService };
